<template>
  <div class="main-panel-menu" data-testid="main-nav">
    <PanelMenu
      v-bind="attrs"
      :class="customClass"
      :model="processedModel"
      :expandedKeys="localExpandedKeys"
      :pt="pt"
      @update:expanded-keys="updateExpandedKeys"
    >
      <template #item="{ item }">
        <slot name="item" :item="item">
          <div :id="item.key" :class="['p-panelmenu-item', `depth-${item.depth}`]" role="button">
            <div
              v-if="item.route"
              :id="item.key"
              class="p-panelmenu-content"
              role="button"
              :title="item.key"
              @contextmenu.prevent="showContextMenu($event, item)"
            >
              <router-link
                v-if="item.route"
                v-ripple
                :to="item.route"
                :data-testid="`nav-link-${item.key}`"
                :data-nav-key="item.key"
                :data-nav-depth="item.depth"
                :class="[
                  isActive(item.route) ? 'active-link' : 'no-active-link',
                  'p-panelmenu-item-link',
                ]"
                @click="handleMenuItemClick"
              >
                <span v-if="item.icon" :class="[item.icon, 'p-panelmenu-item-icon']" />
                <span class="p-panelmenu-item-label">{{ item.label }}</span>
              </router-link>
            </div>

            <div v-else :id="item.key" class="p-panelmenu-header" role="button">
              <a
                v-ripple
                class="p-panelmenu-header-link"
                :data-testid="`nav-group-${item.key}`"
                :data-nav-key="item.key"
                :data-nav-depth="item.depth"
                :data-expanded="item.key ? !!localExpandedKeys[item.key] : false"
                :title="item.key"
              >
                <span
                  v-if="item.icon && item.depth !== 0"
                  :class="[item.icon, 'p-panelmenu-header-icon']"
                />
                <span class="p-panelmenu-header-label">{{ item.label }}</span>
                <Badge v-if="item.badge" class="ml-auto" :value="item.badge" />
                <span
                  v-if="item.items"
                  :class="[
                    'pi ml-auto',
                    item.key && localExpandedKeys[item.key] ? 'pi-angle-down' : 'pi-angle-right',
                    'p-panelmenu-header-toggle',
                  ]"
                />
              </a>
            </div>
          </div>
        </slot>
      </template>
    </PanelMenu>
    <ContextMenu ref="contextMenu" :model="contextMenuItems" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue';
import { usePrimeVue } from 'primevue/config';
import ContextMenu from 'primevue/contextmenu';
import PanelMenu from 'primevue/panelmenu';

import { useFavoritesStore } from '@/stores/favorites';
import { useViewportStore } from '@/stores/viewport';
import type { PanelMenuItem } from '@/router/navigation';
const favoritesStore = useFavoritesStore();
const viewportStore = useViewportStore();

import { useI18n } from 'vue-i18n';

import { useNavigation } from '@/composables/useRoute';

const { isActive } = useNavigation();

const primevueConfig = usePrimeVue();
const mainMenuPanel = primevueConfig.config.pt.mainMenuPanel;

const props = defineProps<{
  model: PanelMenuItem[];
  expandedKeys: Record<string, boolean>;
  pt?: object;
  customClass?: string;
}>();

const pt = {
  ...mainMenuPanel,
  ...props.pt,
};

const { t } = useI18n();

const emit = defineEmits(['update:expandedKeys']);

const attrs = useAttrs();

const localExpandedKeys = ref({ ...props.expandedKeys });

const addDepthToMenu = (menu: PanelMenuItem[], depth = 0): PanelMenuItem[] => {
  if (!menu) return [];

  return menu.map((item) => ({
    ...item,
    depth,
    items: Array.isArray(item.items) ? addDepthToMenu(item.items, depth + 1) : [],
  }));
};

const processedModel = computed(() => addDepthToMenu(props.model));

watch(
  () => props.expandedKeys,
  (newVal) => {
    localExpandedKeys.value = { ...newVal };
  },
  { deep: true },
);

const updateExpandedKeys = (newKeys: Record<string, boolean>) => {
  emit('update:expandedKeys', newKeys);
};

const handleMenuItemClick = () => {
  // Close mobile sidebar when menu item clicked
  if (viewportStore.isMobile) {
    viewportStore.closeMobileSidebar();
  }
};

const contextMenu = ref();
const currentItem = ref();

const showContextMenu = (event: MouseEvent, item: any) => {
  currentItem.value = item;
  if (contextMenu.value) {
    contextMenu.value.show(event);
  }
};

const contextMenuItems = computed(() => {
  if (!currentItem.value?.key) return [];
  const isFavorite = favoritesStore.favorites.includes(currentItem.value.key);
  return [
    {
      label: isFavorite ? t('labels.favRemove') : t('labels.favAdd'),
      icon: isFavorite ? 'pi pi-star-fill' : 'pi pi-star',
      command: () => {
        if (isFavorite) {
          favoritesStore.removeFavorite(currentItem.value?.key || '');
          if (!favoritesStore.favorites.length) {
            expandRootItems();
          }
        } else {
          favoritesStore.addFavorite(currentItem.value?.key || '');
          expandRootByIndex(0); // todo: wait for favorites to be created before opening
        }
      },
    },
  ];
});

// Methods for navigation
const toggleAll = () => {
  if (Object.keys(localExpandedKeys.value).length) {
    collapseAll();
  } else {
    expandAll();
  }
};

const expandAll = () => {
  const expandNode = (node: any) => {
    if (node.items && node.items.length) {
      localExpandedKeys.value[node.key] = true;
      node.items.forEach(expandNode);
    }
  };
  processedModel.value.forEach(expandNode);
  emit('update:expandedKeys', { ...localExpandedKeys.value });
};

const collapseAll = () => {
  localExpandedKeys.value = {};
  emit('update:expandedKeys', {});
};

const expandRootItems = () => {
  localExpandedKeys.value = {};
  processedModel.value.forEach((node) => {
    localExpandedKeys.value[node.key] = true;
  });
  emit('update:expandedKeys', { ...localExpandedKeys.value });
};

const expandRootByIndex = (index: number) => {
  localExpandedKeys.value = {};

  if (index >= 0 && index < processedModel.value.length) {
    const node = processedModel.value[index];
    if (node) {
      localExpandedKeys.value[node.key] = true;
    }
  }

  emit('update:expandedKeys', { ...localExpandedKeys.value });
};

defineExpose({
  toggleAll,
  expandAll,
  collapseAll,
  expandRootItems,
  expandRootByIndex,
});
</script>
